/**
 * Customer Dashboard
 *
 * Shows the customer's assigned audit projects with status badges,
 * upload counts, and a quick link to upload files for each project.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderKanban, UploadCloud, FileText, Shield, Lock, KeyRound, Database, ChevronRight } from 'lucide-react';
import { StatusBadge, Skeleton } from '@connor-music/shared';
import { customerApiClient } from '@/services/api';
import type { Project } from '@shared/types';

export default function Dashboard() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await customerApiClient.getMyProjects();
        setProjects(data);
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadProjects();
  }, []);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Your Audit Projects</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Upload reports and supporting documentation for these projects.
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1 space-y-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-4 w-48" />
                      <Skeleton className="h-5 w-24 rounded-full" />
                    </div>
                    <Skeleton className="h-3 w-64" />
                  </div>
                  <Skeleton className="h-7 w-32 rounded-lg" />
                </div>
              </div>
            ))
          ) : projects.length === 0 ? (
            <div className="p-12 text-center">
              <FolderKanban className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No projects assigned</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                You haven't been assigned to any audit projects yet. Please contact your audit coordinator if you expected to see a project here.
              </p>
            </div>
          ) : (
            projects.map((project) => (
              <div key={project.id} className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4">
                  {/* Project info */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {project.name}
                      </p>
                      <StatusBadge status={project.status} type="project" />
                    </div>

                    {project.description && (
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                        {project.description}
                      </p>
                    )}

                    {/* Upload count */}
                    {project.upload_count != null && project.upload_count > 0 && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                        <FileText className="h-3.5 w-3.5" />
                        <span>
                          {project.upload_count} file{project.upload_count !== 1 ? 's' : ''} uploaded
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Upload action */}
                  <Link
                    to={`/projects/${project.id}/upload`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-primary-500 dark:hover:bg-primary-600 dark:focus:ring-offset-gray-900 flex-shrink-0"
                  >
                    <UploadCloud className="h-3.5 w-3.5" />
                    Submit Audit Data
                  </Link>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {/* Security summary card */}
      <Link
        to="/security"
        className="mt-8 block group rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all p-5"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 h-12 w-12 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Security & Compliance
              </h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                Enterprise-grade protection for your audit data
              </p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
        </div>
        <div className="mt-4 flex items-center gap-6 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1.5">
            <Lock className="h-3.5 w-3.5 text-green-500 dark:text-green-400" />
            AES-256 Encryption
          </span>
          <span className="flex items-center gap-1.5">
            <KeyRound className="h-3.5 w-3.5 text-blue-500 dark:text-blue-400" />
            MFA Required
          </span>
          <span className="flex items-center gap-1.5">
            <Database className="h-3.5 w-3.5 text-purple-500 dark:text-purple-400" />
            Isolated Storage
          </span>
        </div>
      </Link>
    </div>
  );
}
